import { ENodeInverter } from '@vae/server/src/interfaces/eNode/eNodeInverter.interface'
import React, { useContext, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { ResourceType } from '../../@types/i18n'
import ModeSwitch, { ModeOption } from '../../components/ModeSwitch'
import TimeCard from '../../components/TimeCard'
import Button from '../../components/actions/Button'
import { useInfoBox } from '../../components/infoBox/useInfoBox'
import { AppContext } from '../../contexts/AppContext'
import { UserContext } from '../../contexts/UserContext'
import useUser from '../../contexts/hooks/useUser'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { trpc } from '../../modules/trpc'
import { getFontSize } from '../../utils/getFontSize'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import { useChartTimeConsumptionChart } from '../electricity/charts/consumptionChart/hooks/useChartTimeConsumptionChart'
import './Devices.scss'
import ConsumptionElementsDistributionChart from './components/ConsumptionElementsDistribution'
import PercentageLinkCard from './components/PercentageLinkCard'

interface Props {
	children: React.ReactNode
}

const getPeriod = (span: 'day' | 'week' | 'month' | 'year' | 'hour') => {
	switch (span) {
		case 'day':
			return '2'
		case 'week':
			return '3'
		case 'month':
			return '4'
		case 'year':
			return '5'
		case 'hour':
			return '2'
		default:
			return '2'
	}
}

const Devices: React.FC<Props> = () => {
	const { isAnonymousUser, logOut } = useContext(UserContext)
	const object = useContext(ObjectContext)
	const { isDarkMode } = useContext(AppContext)

	const modeState = useState<'consumption' | 'consumption-distribution'>('consumption')

	const time = useChartTimeConsumptionChart()
	const { user } = useUser()
	const history = useHistory()

	const { t } = useTranslation('pages_consumptionElements')
	const { t: tc } = useTranslation('consumptionElements')
	const { t: te } = useTranslation('pages_electricity')

	const { data: data, isFetching } = trpc.objects.getConsumptionElements.useQuery(
		{
			objectId: object.id,
			from: time.startTime?.toISOString() ?? '',
			to: time.endTime?.toISOString() ?? '',
			period: getPeriod(time.activeTimespan),
		},
		{ enabled: !isAnonymousUser && object.id !== 0 }
	)

	const { data: inverters, isLoading: invertersLoading } = trpc.eNode.inverter.getAll.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{
			staleTime: 2 * 1000 * 60, // 2 mins
			enabled: Boolean(user?.id) && user?.panelLinked,
		}
	)
	const { data: hvacs, isLoading: hvacsLoading } = trpc.eNode.hvac.getAll.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{
			enabled: Boolean(user?.hashedPid) && user?.hvacLinked, // production
			staleTime: 10 * 1000 * 30, // 1mins
		}
	)

	const { data: vehicles } = trpc.eNode.vehicles.get.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{
			enabled: Boolean(user?.hashedPid) && user?.vehicleLinked, // production
			staleTime: 10 * 1000 * 30, // 1mins
		}
	)

	const { data: userData } = trpc.eNode.user.get.useQuery(
		{
			userId: user?.hashedPid ?? '',
		},
		{
			enabled: Boolean(user) && Boolean(user?.hashedPid) && user?.vehicleLinked,
		}
	)

	const anchorRef1 = useRef<HTMLIonButtonElement>(null)
	const anchorRef2 = useRef<HTMLDivElement>(null)

	const { open: openInfoBox, InfoBoxComponent } = useInfoBox<
		HTMLIonButtonElement | HTMLDivElement
	>()

	const handleInfoClick = () => {
		openInfoBox([
			{
				content: <div style={{ padding: '0.6em' }}>{t('addDeviceInfo')}</div>,
				anchor: anchorRef1,
			},
			{
				content: <div style={{ padding: '0.6em' }}>{t('consumptionElementsInfo')}</div>,
				anchor: anchorRef2,
			},
			{
				content: <div style={{ padding: '0.6em' }}>{te('consumptionElementsInfo')}</div>,
				anchor: anchorRef2,
			},
		])
	}

	const modeOptions: ModeOption<'consumption' | 'consumption-distribution'>[] = [
		{
			label: t('totalConsumption'),
			mode: 'consumption',
		},
		{
			label: t('consumptionDistribution'),
			mode: 'consumption-distribution',
		},
	]

	const onModeChange = (mode: 'consumption' | 'consumption-distribution') => {
		modeState[1](mode)
	}

	const fontSize = getFontSize()

	const consumptionBarChartOptions = {
		maintainAspectRatio: false,
		scales: {
			x: {
				ticks: {
					color: isDarkMode ? '#e3e3e3' : '#858585',
				},
				grid: {
					color: isDarkMode ? '#144448' : '#f5f5f5',
				},
			},
			y: {
				ticks: {
					color: isDarkMode ? '#3cb49f' : '#127d27',
				},
				grid: {
					color: isDarkMode ? '#144448' : '#f5f5f5',
				},
			},
		},
		plugins: {
			tooltip: {
				titleFont: {
					size: fontSize + 2,
					weight: 'bold' as const,
				},
				bodyFont: {
					size: fontSize,
				},
			},
			legend: {
				labels: {
					color: isDarkMode ? '#e3e3e3' : '#858585',
				},
			},
		},
	}

	// Helper function to check if a vendor connection has failed
	const hasConnectionFailed = (deviceName: string, type: 'vehicle' | 'inverter' | 'hvac') => {
		if (!userData?.linkedVendors || !deviceName) return false

		return userData.linkedVendors.some(
			vendor =>
				deviceName.toUpperCase().startsWith(vendor.vendor.toUpperCase()) &&
				vendor.vendorType === type &&
				!vendor.isValid
		)
	}

	// Helper function to get device display name
	const getDeviceDisplayName = (brand: string, model: string) => {
		return `${brand} ${model}`
	}

	// Render EV consumption cards
	const renderEVCards = () => {
		if (!vehicles || vehicles.length === 0) return null
		return vehicles.map(vehicle => (
			<PercentageLinkCard
				isDarkMode={isDarkMode}
				key={vehicle.id}
				percentage={0}
				chevron={true}
				variant={'blue'}
				clickHandler={() => {
					history.push(`/devices/electric-car/${vehicle.id}`, {
						element: vehicle,
					})
				}}
				texts={{
					title:
						vehicle.information.displayName ||
						`${vehicle.information.brand} ${vehicle.information.model}`,
					subText: hasConnectionFailed(
						vehicle.information.displayName ||
							`${vehicle.information.brand} ${vehicle.information.model}`,
						'vehicle'
					)
						? t('connectionFailed')
						: ``,
				}}
			/>
		))
	}

	// Render inverter/solar panel cards
	const renderInverterCards = () => {
		if (invertersLoading || !inverters) return null

		return inverters.map((inverter: ENodeInverter) => {
			if (!inverter.id) return null

			const deviceName = getDeviceDisplayName(
				inverter.information.brand,
				inverter.information.model
			)
			const productionRate = inverter.productionState.productionRate?.toFixed(1) ?? '0'

			return (
				<PercentageLinkCard
					isDarkMode={isDarkMode}
					key={inverter.id}
					clickHandler={() => history.push(`/devices/solar-panel/${inverter.id}`)}
					variant={'blue'}
					percentage={0}
					chevron={Boolean(inverter.id)}
					texts={{
						title: deviceName,
						subText: hasConnectionFailed(deviceName, 'inverter')
							? t('connectionFailed')
							: `${productionRate} kW`,
					}}
				/>
			)
		})
	}

	// Render HVAC cards
	const renderHvacCards = () => {
		if (hvacsLoading || !hvacs?.data || hvacs.data.length === 0) return null

		return hvacs.data
			.sort((a, b) => a.information.brand.localeCompare(b.information.brand))
			.map(hvac => {
				const deviceName = getDeviceDisplayName(
					hvac.information.brand,
					hvac.information.model ?? ''
				)

				return (
					<PercentageLinkCard
						isDarkMode={isDarkMode}
						key={hvac.id}
						clickHandler={() => {
							history.push(`/devices/hvac/${hvac.id}`)
						}}
						variant={'blue'}
						percentage={0}
						chevron={Boolean(hvac.id)}
						texts={{
							title: deviceName,
							subText: hasConnectionFailed(deviceName, 'hvac') ? t('connectionFailed') : '',
						}}
					/>
				)
			})
	}

	const onClickRegister = () => {
		logOut()
		history.push('/register')
	}

	return (
		<PageWrapper page='devices' appBarOptions={{ onInfoClick: handleInfoClick }}>
			<ModeSwitch
				options={modeOptions}
				currentMode={modeState[0]}
				onModeChange={onModeChange}
				shadow
			/>

			<div className='flex flex-col gap-md'>
				{modeState[0] === 'consumption' && (
					<TimeCard loading={isFetching} time={time}>
						{isAnonymousUser && (
							<div className='absolute text-[0.6rem] text-center px-[15%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-[3]'>
								{t('noData')}
							</div>
						)}
						<Bar
							options={consumptionBarChartOptions}
							data={{
								labels: (data ?? []).map(x =>
									x.isCar ? x.name : tc(x.name as keyof ResourceType['consumptionElements'])
								),
								datasets: [
									{
										label: 'kWh',
										data: (data ?? []).map(x => x.kwh),
										backgroundColor: isDarkMode ? '#3cb49f' : '#52ae32',
									},
								],
							}}
						/>
					</TimeCard>
				)}

				{modeState[0] === 'consumption-distribution' && (
					<TimeCard loading={isFetching} time={time}>
						{isAnonymousUser && (
							<div className='absolute text-[0.6rem] text-center px-[15%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 scale-[3]'>
								{t('noData')}
							</div>
						)}
						<ConsumptionElementsDistributionChart
							data={data ?? []}
							// eslint-disable-next-line @typescript-eslint/no-empty-function
							setActiveElement={() => {}}
							noData={!!data}
						/>
					</TimeCard>
				)}

				<div
					className='link-cards w-full gap-sm grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3'
					ref={anchorRef2}
				>
					{(data ?? [])
						.filter(x => !x.isCar)
						.map(consumptionElement => {
							const belowRange = consumptionElement.confidencePct / 100
							const aboveRange = 1 - belowRange + 1
							const errorMargin = 100 - consumptionElement.confidencePct
							const rangeColor =
								consumptionElement.confidencePct > 66
									? 'green'
									: consumptionElement.confidencePct > 33
										? '#E67E22'
										: '#C62828'

							return (
								<PercentageLinkCard
									isDarkMode={isDarkMode}
									key={consumptionElement.name}
									percentage={consumptionElement.percentage}
									chevron={false}
									showHelp={true}
									helptext={t(
										consumptionElement.name as keyof ResourceType['pages_consumptionElements']
									)}
									texts={{
										title:
											tc(consumptionElement.name as keyof ResourceType['consumptionElements']) ||
											consumptionElement.name,
										accuracy: {
											text: t('errorMargin', { errorMargin: errorMargin.toFixed(1) }),
											color: rangeColor,
										},
										range: t('range', {
											below: (consumptionElement.kwh * belowRange).toFixed(1),
											above: (consumptionElement.kwh * aboveRange).toFixed(1),
										}),
										subText:
											typeof consumptionElement.name === 'string' &&
											userData?.linkedVendors?.some(
												vendor => consumptionElement.name && !vendor.isValid
											)
												? t('connectionFailed')
												: `${consumptionElement.kwh.toFixed(1)} kWh`,
									}}
								/>
							)
						})}

					{user && user.vehicleLinked && renderEVCards()}

					{user && user.panelLinked && renderInverterCards()}
					{user && user.hvacLinked && renderHvacCards()}
				</div>

				{InfoBoxComponent}

				{isAnonymousUser && (
					<div
						onClick={onClickRegister}
						className='mx-auto p-2 rounded text-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800  text-text-dark dark:text-white'
					>
						{t('registeringRequired')}
					</div>
				)}
				<Button
					disabled={isAnonymousUser}
					ref={anchorRef1}
					onClick={() => history.push('/devices/add-device')}
					className='w-full m-0	'
				>
					{t('lisaa')}
				</Button>
			</div>
		</PageWrapper>
	)
}
export default Devices
